<template>
  <div
    v-if="isVisibleTarget"
    :class="['box']"
    :dir="isRightToLeft ? 'rtl' : 'ltr'"
    @mouseenter="handleMouseEnter"
    @mouseleave="showMenu = false"
    @contextmenu.prevent="copyText"
  >
    <div class="container-chatting" :dir="isRightToLeft ? 'rtl' : 'ltr'">
      <div
        ref="contentContainerSrc"
        :style="{
          '--font-multiplier': String(fontMultiplier),
        }"
        :dir="isRightToLeft ? 'rtl' : 'ltr'"
      >
        <p
          v-for="content in targetContents"
          :key="getKey(content)"
          :class="{
            'origin-text': true,
            'cursor-pointer': isReceivedAudio,
            'sound-playing': isPlaying,
            'highlight-origin': content.highlight,
            'highlight-origin-fadeout': content.fadeout,
          }"
          :dir="isRightToLeft ? 'rtl' : 'ltr'"
          :style="styleParagraph"
          @click="emit('playAudioManual', isPlaying, message)"
        >
          <AnimationTyping
            :is-visible="content.highlight"
            :text="content.content"
            :dir="isRightToLeft ? 'rtl' : 'ltr'"
            is-using-new-line
            :is-keep-all="!isLanguageNoSpace(content.languageCode)"
          />
          <pre v-if="!content.highlight" class="pre-message">{{ content.content }}</pre>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, provide, ref, toRef, watch, CSSProperties } from 'vue';

import AnimationTyping from '@/components/animations/AnimationTyping.vue';
import { useMessageBubbleCommon } from '@/composables/domains/chat/useMessageBubbleCommon';
import { useMessageTextCommon } from '@/composables/domains/chat/useMessageTextCommon';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { ChatMessage, MessageText } from '@/types/socket.js';
import {isLanguageNoSpace} from '@/utils/chat/language';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import {
  ij_callLogId,
  ij_isLastIsOwn,
  ij_setEmptyBubbleMy,
} from '@/utils/injectionKeys.js';

const props = withDefaults(
  defineProps<{
    messages: ChatMessage[];
    message: ChatMessage;
    lastCallLogId?: string;
  }>(),
  {
    messages: () => [],
    message: () => ({}) as ChatMessage,
    longVoice: false,
  }
);
const audioStore = useAudioStore();
const userStore = useUserStore();

const setEmptyBubbleMy = inject(ij_setEmptyBubbleMy);

const contentContainer = ref<HTMLElement>();
const isHidden = ref<boolean>(false);
const showMenu = ref<boolean>(false);

const emit = defineEmits<{
  playAudioManual: [isPlaying: boolean, message: ChatMessage];
}>();

const { updateContainerHeight, shrinkBubble, getKey } = useMessageTextCommon({
  contentContainer,
});

const { handleChangeReset } = useMessageBubbleCommon({
  chatMessage: toRef(props, 'message'),
  isHidden,
  setEmptyBubble: setEmptyBubbleMy,
});

// 오른쪽 정렬 언어인지 반환
const isRightToLeft = computed(() => {
  const listLangAlignRight: LanguageCode[] = ['ar']; // 아랍어,
  return listLangAlignRight.some((lang) =>
    props.message.tgtLanguageSet.has(lang)
  );
});

// 타겟 컨텐츠
const targetContents = computed<MessageText[]>(() => {
  const { tgt: { contents = [] } = { contents: [] } } = props.message;
  return contents;
});

// 음생 재생 여부
const isPlaying = computed<boolean>(() => {
  return (
    audioStore.playState == 2 &&
    audioStore.playCallLogId === props.message.callLogId
  );
});

// 글자 크기 배율
const fontMultiplier = computed<number>(() => {
  return userStore.userPreferences.textSizeMultiplier;
});

// 나의 발화 중 이 메세지가 최신인지
const isLastMessage = computed<boolean>(() => {
  const messageLastIsOwn = props.messages.at(-1);
  if (!messageLastIsOwn) return false;
  return messageLastIsOwn.callLogId === props.message.callLogId;
});

// 오디오 수신 여부
const isReceivedAudio = computed<boolean>(() => {
  if (!isLastIsOwn.value) return true;
  const isAudioComplete = props.message.isAudioComplete === true;
  const isReset = props.message.reset === '1';
  return isAudioComplete || isReset;
});

// 번역문 보임 여부
const isVisibleTarget = computed<boolean>(() => {
  return (
    targetContents.value &&
    targetContents.value.length > 0 &&
    targetContents.value.some(({ content = '' }) => !!content.trim())
  );
});

// 나의 발화 중 이 메세지가 최신인지
const isLastIsOwn = computed<boolean>(() => {
  const lastMessage = props.messages.findLast((item) => item.isOwn) || {};
  return lastMessage?.callLogId === props.message.callLogId;
});

const styleParagraph = computed<CSSProperties>(() => {
  return {
    wordBreak: isEveryLanguageSpaced.value ? 'keep-all':'break-all',
  };
});

const isEveryLanguageSpaced = computed(() => {
  return [...props.message.tgtLanguageSet].every((code) => !isLanguageNoSpace(code));
});

const handleMouseEnter = () => {
  const { os } = getBrowserAndDeviceInfo();
  showMenu.value = os !== 'iOS';
};

const copyText = () => {
  if (!document.hasFocus()) {
    console.warn('Document not focused — copy aborted');
    return;
  }
  
  const contentsMapped = props.message.tgt.contents.map(
    (item) => {if(!item.highlight) return item.content}
  );
  const textTotal = contentsMapped.join('')
  navigator.clipboard.writeText(textTotal);
}

watch(
  () => props.message.reset,
  (resetNew) => {
    handleChangeReset(resetNew);
  },
  { immediate: true }
);

// 글자 크기 변경 시 높이 조절
watch(fontMultiplier, () => {
  shrinkBubble(); // 버블 줄이기
});

// 내가 더이상 마지막 상대 발화가 아니고 버블에 공백이 있을 때 버블 줄이기 발동
watch(
  isLastMessage,
  (newVal) => {
    if (!newVal) {
      shrinkBubble(); // 버블 줄이기
    }
  },
  { flush: 'post' }
);

provide(ij_callLogId, props.message.callLogId);
provide(ij_isLastIsOwn, isLastIsOwn);
</script>

<style scoped lang="scss">
.loading {
  width: fit-content;
  align-self: flex-end;
  margin-bottom: 0.75rem;
  padding: 0.25rem 1.5rem 0.75rem 1rem;
  border-radius: 8px;
}

.box {
  display: flex;
  position: relative;
  min-height: unset;
  padding: 0 1rem;
  margin-bottom: 0.75rem;
}

.box.last-message {
  min-height: 40dvh;
}

.container-chatting {
  display: flex;
  width: 100%;
  height: fit-content;
  flex-shrink: 0;

  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  text-align: start;
}

/* 오리지널 - 텍스트 */
.origin-text {
  display: inline;
  line-height: 160%;
  color: var(--font-default);
  border-radius: 4px;
  font-size: calc($font-size-p * var(--font-multiplier, 1));

  &.cursor-pointer {
    cursor: pointer;
  }
  unicode-bidi: isolate;
}

/* 오리지널 - 소리 Play */
.sound-playing {
  color: var(--font-default);
}

/* 오리지널 - 통역/입력중인 텍스트 */
.highlight-origin {
  display: inline;
  color: var(--font-default);
  animation: none;
  opacity: 0.5;
}

/* 오리지널 - 통역 완료된 텍스트 */
.highlight-origin-fadeout {
  display: inline;
  color: var(--font-default);
  animation: fadeIn 3s ease-in-out forwards;
}

.pre-message {
  white-space: pre-line;
  font-family: inherit;
  font-size: inherit;
  display: inline;
  overflow-wrap: anywhere;
  margin: 0;
  padding: 0;
}

@keyframes fadeIn {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
